/**
 * 強制收回 DeviceLease + 倒數（M2 D-2 launch-blocker / AC-D2-14）。
 *
 * 對應 BE POST /admin/leases/:lease_id/forced-reclaim
 *        與 GET /admin/leases/:lease_id/forced-reclaim-log。
 *
 * AC-D2-14 重點：
 *   - reason 必填（表單 + BE DTO 雙驗）
 *   - 強制收回後留 forced_reclaim_log 稽核
 *   - 回傳 reclaim_available_at → UI 即時倒數（替代裝置可取新 lease 之時點）
 */

import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import {
  forcedReclaimLease,
  getForcedReclaimLog,
  getMe,
  type ForcedReclaimLogEntry,
  type ForcedReclaimResult,
} from '../lib/api-client';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('zh-TW');
  } catch {
    return iso;
  }
}

/** 即時倒數至 targetIso；每秒 tick。視窗過後顯示「已可收回」。 */
function Countdown({ targetIso }: { targetIso: string }): JSX.Element {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const target = new Date(targetIso).getTime();
  const diffMs = target - now;

  if (Number.isNaN(target)) {
    return <span style={{ color: '#6b7280' }}>—</span>;
  }
  if (diffMs <= 0) {
    return (
      <span style={{ color: '#065f46', fontWeight: 700 }}>
        已過隔離視窗 — 替代裝置可取得新 lease
      </span>
    );
  }
  const totalSec = Math.floor(diffMs / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const parts = [h > 0 ? `${h} 時` : '', m > 0 || h > 0 ? `${m} 分` : '', `${s} 秒`].filter(
    Boolean,
  );
  return (
    <span style={{ color: '#b91c1c', fontWeight: 700, fontFamily: 'monospace', fontSize: 18 }}>
      {parts.join(' ')}
    </span>
  );
}

export function ForcedReclaimPage(): JSX.Element {
  const [leaseIdInput, setLeaseIdInput] = useState('');
  const [reasonInput, setReasonInput] = useState('');
  /** 已成功收回 / 正在查 log 的 lease_id */
  const [activeLeaseId, setActiveLeaseId] = useState<string | null>(null);

  const meQuery = useQuery({ queryKey: ['auth.me'], queryFn: getMe });

  const reclaimMutation = useMutation({
    mutationFn: (payload: { leaseId: string; reason: string }) => {
      const userId = meQuery.data?.user_id;
      if (!userId) {
        return Promise.reject(new Error('無法取得當前使用者 ID（getMe 尚未完成）'));
      }
      return forcedReclaimLease(payload.leaseId, {
        reason: payload.reason,
        actor_user_id: userId,
      });
    },
    onSuccess: (result: ForcedReclaimResult) => {
      setActiveLeaseId(result.lease_id);
    },
  });

  const logQuery = useQuery({
    queryKey: ['forced-reclaim-log', activeLeaseId],
    queryFn: () => getForcedReclaimLog(activeLeaseId as string),
    enabled: activeLeaseId !== null,
  });

  const trimmedLeaseId = leaseIdInput.trim();
  const trimmedReason = reasonInput.trim();
  const leaseIdValid = UUID_RE.test(trimmedLeaseId);
  const canSubmit =
    leaseIdValid &&
    trimmedReason.length > 0 &&
    !reclaimMutation.isPending &&
    Boolean(meQuery.data?.user_id);

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (!canSubmit) return;
    if (
      window.confirm(
        `確定要強制收回此 lease？\n` +
          `Lease ID：${trimmedLeaseId}\n` +
          `原因：${trimmedReason}\n\n` +
          `收回後該裝置立即停止寫入；替代裝置須等隔離視窗過後才能取得新 lease。\n` +
          `操作會寫入 forced_reclaim_log + audit log，且不可復原。`,
      )
    ) {
      reclaimMutation.mutate({ leaseId: trimmedLeaseId, reason: trimmedReason });
    }
  };

  const result = reclaimMutation.data;
  const logRows: ForcedReclaimLogEntry[] = logQuery.data?.rows ?? [];

  return (
    <div>
      <div className="page-head">
        <h1 className="page-head__title">強制收回 DeviceLease（D-2 / AC-D2-14）</h1>
      </div>
      <p style={{ color: '#6b7280' }}>
        強制收回某裝置之 lease：原因必填、留 forced_reclaim_log 稽核；收回後替代裝置須等隔離視窗
        （quarantine_window）過後才能取得新 lease，下方即時倒數顯示可收回時點。
      </p>

      {meQuery.error && (
        <div className="error-banner">
          無法取得當前使用者：{(meQuery.error as Error).message}（強制收回功能將停用）
        </div>
      )}

      {/* 收回表單 */}
      <form
        onSubmit={handleSubmit}
        style={{
          background: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: 4,
          padding: 16,
          margin: '12px 0',
          maxWidth: 560,
        }}
      >
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
            Lease ID（UUID）
          </label>
          <input
            type="text"
            value={leaseIdInput}
            onChange={(e) => setLeaseIdInput(e.target.value)}
            placeholder="00000000-0000-0000-0000-000000000000"
            style={{ width: '100%', fontFamily: 'monospace', padding: 6 }}
          />
          {trimmedLeaseId.length > 0 && !leaseIdValid && (
            <div style={{ color: '#b91c1c', fontSize: 12, marginTop: 2 }}>格式不是合法 UUID</div>
          )}
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
            收回原因（必填）
          </label>
          <textarea
            value={reasonInput}
            onChange={(e) => setReasonInput(e.target.value)}
            rows={3}
            maxLength={500}
            placeholder="例：裝置遺失 / 裝置故障無回應 / 班次調度換機"
            style={{ width: '100%', padding: 6, resize: 'vertical' }}
          />
          <div style={{ fontSize: 12, color: '#6b7280' }}>{trimmedReason.length} / 500</div>
        </div>
        <button type="submit" disabled={!canSubmit}>
          {reclaimMutation.isPending ? '收回中…' : '強制收回'}
        </button>
      </form>

      {reclaimMutation.error && (
        <div className="error-banner">強制收回失敗：{(reclaimMutation.error as Error).message}</div>
      )}

      {/* 收回結果 + 倒數 */}
      {result && (
        <section
          style={{
            background: '#fff',
            border: '1px solid #fca5a5',
            borderRadius: 4,
            padding: 16,
            margin: '12px 0',
            maxWidth: 560,
          }}
        >
          <h2 style={{ marginTop: 0, fontSize: 16 }}>✅ 已強制收回</h2>
          <dl
            style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 12px', margin: 0 }}
          >
            <dt style={{ color: '#6b7280', fontSize: 13 }}>Lease ID</dt>
            <dd style={{ margin: 0, fontFamily: 'monospace', fontSize: 12 }}>{result.lease_id}</dd>
            <dt style={{ color: '#6b7280', fontSize: 13 }}>班次 / 裝置</dt>
            <dd style={{ margin: 0, fontFamily: 'monospace', fontSize: 12 }}>
              {result.trip_id.slice(0, 8)}… / {result.device_id.slice(0, 8)}…
            </dd>
            <dt style={{ color: '#6b7280', fontSize: 13 }}>原因</dt>
            <dd style={{ margin: 0 }}>{result.reason}</dd>
            <dt style={{ color: '#6b7280', fontSize: 13 }}>隔離視窗</dt>
            <dd style={{ margin: 0 }}>{result.quarantine_window_seconds} 秒</dd>
            <dt style={{ color: '#6b7280', fontSize: 13 }}>可收回時點</dt>
            <dd style={{ margin: 0 }}>{formatDateTime(result.reclaim_available_at)}</dd>
          </dl>
          <div
            style={{
              marginTop: 12,
              paddingTop: 12,
              borderTop: '1px dashed #e5e7eb',
              display: 'flex',
              alignItems: 'baseline',
              gap: 8,
            }}
          >
            <span style={{ fontSize: 13, color: '#6b7280' }}>距替代裝置可取新 lease：</span>
            <Countdown targetIso={result.reclaim_available_at} />
          </div>
        </section>
      )}

      {/* forced_reclaim_log 稽核軌跡 */}
      {activeLeaseId && (
        <section style={{ margin: '12px 0' }}>
          <h2 style={{ fontSize: 16 }}>強制收回稽核紀錄</h2>
          {logQuery.isLoading && <div>載入中…</div>}
          {logQuery.error && (
            <div className="error-banner">{(logQuery.error as Error).message}</div>
          )}
          {logQuery.data && (
            <table>
              <thead>
                <tr>
                  <th>時間</th>
                  <th>原因</th>
                  <th>操作者</th>
                  <th>新 lease</th>
                  <th>隔離視窗</th>
                </tr>
              </thead>
              <tbody>
                {logRows.length === 0 && (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', color: '#6b7280' }}>
                      無稽核紀錄
                    </td>
                  </tr>
                )}
                {logRows.map((row) => (
                  <tr key={row.id}>
                    <td style={{ fontSize: 12 }}>{formatDateTime(row.occurred_at)}</td>
                    <td>{row.reason}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: 12 }}>
                      {row.actor_user_id ? `${row.actor_user_id.slice(0, 8)}…` : '系統'}
                    </td>
                    <td style={{ fontFamily: 'monospace', fontSize: 12 }}>
                      {row.new_lease_id ? `${row.new_lease_id.slice(0, 8)}…` : '—'}
                    </td>
                    <td>
                      {row.quarantine_window_seconds != null
                        ? `${row.quarantine_window_seconds} 秒`
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}
    </div>
  );
}
